import React, { useCallback, ChangeEvent, useState } from 'react';

import { isEmptyString, validEmail } from 'Shared/helpers';
import TextField from 'Shared/components/TextField/TextField';

// Hooks
import { useOrganization } from 'Shared/utils/hooks/use-organization';
import useValidateEmployeeEmail from 'Shared/utils/hooks/use-validate-employee-email';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

import './AddTeammateForm.scss';

const EmailLabel = ({ isEmailMandatory = false }: { isEmailMandatory: boolean }) => {
  const { tr } = useTranslation();
  return (
    <div className="text-field-label text-muted">
      {tr('perform.invitations.add_teammate.email_label')}
      {!isEmailMandatory && <span className="ml-1">{tr('perform.invitations.add_teammate.optional')}</span>}
    </div>
  );
};

type Props = {
  isDirectInvite?: boolean;
  isEmailMandatory?: boolean;
  emailPlaceholder?: string;
  formValues: {
    firstName: string;
    lastName: string;
    email: string;
    personalMessage: string;
  };
  onTextFieldsChange: (event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) => void;
};

function AddTeammateForm({
  isDirectInvite = false,
  isEmailMandatory,
  emailPlaceholder,
  formValues,
  onTextFieldsChange,
}: Props) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { organization } = useOrganization();
  const { isEmployeeEmailOrValidDomain } = useValidateEmployeeEmail();
  const { tr } = useTranslation();

  const getEmailFormatErrorMessage = email => {
    if (!email) return null;
    if (!validEmail(email)) return tr('perform.invitations.add_teammate.errors.invalid_email', email);
    if (!isEmployeeEmailOrValidDomain({ email }))
      return tr('perform.invitations.add_teammate.errors.invalid_domain', organization.pi_allowed_domains.join(', '));
    return null;
  };

  const isEmptyTeammateEmail = useCallback(() => {
    const email = formValues.email;
    if (!email) return true;
    return isEmptyString(email);
  }, [formValues.email]);

  const handleBlurEmailInput = useCallback(
    () => setErrorMessage(getEmailFormatErrorMessage(formValues.email)),
    [formValues.email]
  );

  const effectiveEmailPlaceholder = emailPlaceholder ?? tr('perform.invitations.add_teammate.email_placeholder');

  return (
    <div className="add-teammate-form">
      <div className="d-flex justify-content-between flex-column flex-sm-row align-items-end">
        <div className="w-100 mr-0 mr-sm-2">
          <TextField
            name="firstName"
            type="text"
            label={tr('perform.invitations.add_teammate.name_label_required')}
            placeholder={tr('perform.invitations.add_teammate.first_name_placeholder')}
            value={formValues.firstName}
            onChange={onTextFieldsChange}
          />
        </div>
        <TextField
          name="lastName"
          type="text"
          placeholder={tr('perform.invitations.add_teammate.last_name_placeholder')}
          value={formValues.lastName}
          onChange={onTextFieldsChange}
        />
      </div>

      {!isDirectInvite ? (
        <TextField
          name="email"
          type="text"
          label={<EmailLabel isEmailMandatory={Boolean(isEmailMandatory)} />}
          placeholder={effectiveEmailPlaceholder}
          onBlur={handleBlurEmailInput}
          value={formValues.email}
          onChange={onTextFieldsChange}
        />
      ) : null}
      {errorMessage ? (
        <div className="alert alert-danger p-2 mt-2 mb-2 small w-100" role="alert">
          {errorMessage}
        </div>
      ) : null}
      {!isDirectInvite && !isEmailMandatory ? (
        <div className="add-teammate-form-field-comment text-muted mb-2">
          {tr('perform.invitations.add_teammate.invite_later_hint')}
        </div>
      ) : null}

      {!isEmptyTeammateEmail() ? (
        <div className="d-flex flex-column mt-3">
          <div className="small font-weight-bold text-muted mb-1">
            {tr('perform.invitations.add_teammate.personal_message_label')}
          </div>
          <textarea
            name="personalMessage"
            className="w-100 border rounded p-2"
            value={formValues.personalMessage}
            onChange={onTextFieldsChange}
          />
        </div>
      ) : null}
    </div>
  );
}

export default AddTeammateForm;
